import * as React from "react";
import {
  Box,
  Center,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  chakra,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  EmrDocument,
  EmrPlus,
  EmrRemove,
  EmrTrashcan,
  SiAddCircle,
  SiClear,
} from "@medstonetech/slate-icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "react-query";
import { format } from "date-fns";
import { useGetUserDetails } from "api";
import { useFileUrl } from "hooks";
import { formMessages } from "messages";
import {
  useAddendumAttachmentFile,
  useAddendums,
  useCreateAddendum,
  useDeleteAddendum,
  useDeleteAddendumAttachment,
  useUpdateAddendum,
  useUploadAddendumAttachment,
} from "modules/charts-shared/api";
import {
  Button,
  Card,
  Loading,
  PdfViewer,
  PdfViewerActivePage,
  PdfViewerContainer,
  PdfViewerPageController,
  Textarea,
  UserAvatar,
  WarningDialog,
} from "shared";
import { USERID_CLAIMS } from "system-constants";
import {
  Addendum,
  AddendumAttachment,
  AddendumAuthor,
  AddendumsResponse,
} from "types";
import { extractApiErrorMessage, isImage } from "utils";

type DraftAttachment =
  | { kind: "existing"; key: string; attachment: AddendumAttachment }
  | { kind: "pending"; key: string; file: File };

type DraftAddendum = {
  key: string;
  id: string | null;
  text: string;
  savedText: string;
  createdAt: string;
  createdBy: AddendumAuthor;
  isEditable: boolean;
  attachments: DraftAttachment[];
  removedAttachmentIds: string[];
};

type AddendumsDraft = {
  addendums: DraftAddendum[];
  removedAddendumIds: string[];
};

type AddendumsModalProps = {
  encounterId: string;
  isOpen: boolean;
  onClose: () => void;
};

type AddendumsModalContentProps = Omit<AddendumsModalProps, "isOpen">;

type AddendumsEditorProps = AddendumsModalContentProps & {
  initialData: AddendumsResponse;
};

type AddendumCardProps = {
  addendum: DraftAddendum;
  isDisabled: boolean;
  onChangeText: (text: string) => void;
  onDelete: () => void;
  onAddFiles: (files: File[]) => void;
  onRemoveAttachment: (attachment: DraftAttachment) => void;
};

type AttachmentItemProps = {
  attachment: DraftAttachment;
};

type AttachmentThumbnailProps = AttachmentItemProps & {
  isSelected: boolean;
  onSelect: () => void;
};

const ADDENDUM_GRID_COLUMNS = "280px 200px 1fr";
const ALLOWED_ATTACHMENT_TYPES: Record<string, string> = {
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
};
const ATTACHMENT_ACCEPT = Object.keys(ALLOWED_ATTACHMENT_TYPES).join(",");
const MAX_ATTACHMENT_SIZE_IN_MB = 10;
const MAX_ADDENDUM_TEXT_LENGTH = 10000;
const MAX_ATTACHMENT_SIZE = MAX_ATTACHMENT_SIZE_IN_MB * 1024 * 1024;

function isAllowedAttachment(file: File): boolean {
  const extensionIndex = file.name.lastIndexOf(".");
  if (extensionIndex < 0) return false;
  const extension = file.name.slice(extensionIndex).toLowerCase();
  const expectedType: string | undefined = ALLOWED_ATTACHMENT_TYPES[extension];
  return (
    expectedType === file.type.toLowerCase() && file.size <= MAX_ATTACHMENT_SIZE
  );
}
const DEADLINE_NOTE = "Addendums can be added up to 30 days after discharge.";

function formatAddendumDate(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return format(date, "MM-dd-yyyy HH:mm");
}

function toDraftAttachment(attachment: AddendumAttachment): DraftAttachment {
  return { kind: "existing", key: attachment.id, attachment };
}

function toDraftAddendum(addendum: Addendum, canAdd: boolean): DraftAddendum {
  return {
    key: addendum.id,
    id: addendum.id,
    text: addendum.text,
    savedText: addendum.text,
    createdAt: addendum.createdAt,
    createdBy: addendum.createdBy,
    isEditable: canAdd && addendum.canEdit,
    attachments: addendum.attachments.map(toDraftAttachment),
    removedAttachmentIds: [],
  };
}

function isDraftAddendumDirty(addendum: DraftAddendum): boolean {
  return (
    addendum.id === null ||
    addendum.text !== addendum.savedText ||
    addendum.removedAttachmentIds.length > 0 ||
    addendum.attachments.some((attachment) => attachment.kind === "pending")
  );
}

function getAttachmentName(attachment: DraftAttachment): string {
  return attachment.kind === "pending"
    ? attachment.file.name
    : attachment.attachment.name;
}

function useAttachmentFile(
  attachment: DraftAttachment,
  enabled: boolean
): { file: Blob | null; contentType: string; isLoading: boolean } {
  const isExisting = attachment.kind === "existing";
  const attachmentId = isExisting ? attachment.attachment.id : "";
  const { data, isLoading } = useAddendumAttachmentFile(
    attachmentId,
    enabled && isExisting
  );

  if (attachment.kind === "pending") {
    return {
      file: attachment.file,
      contentType: attachment.file.type,
      isLoading: false,
    };
  }

  const file = data?.data ?? null;
  return {
    file,
    contentType: attachment.attachment.contentType ?? file?.type ?? "",
    isLoading: enabled && isLoading,
  };
}

function isImageAttachment(attachment: DraftAttachment): boolean {
  const contentType =
    attachment.kind === "pending"
      ? attachment.file.type
      : attachment.attachment.contentType ?? "";
  return Boolean(isImage(contentType));
}

function AttachmentThumbnail(props: AttachmentThumbnailProps) {
  const { attachment, isSelected, onSelect } = props;
  const showImage = isImageAttachment(attachment);
  const { file } = useAttachmentFile(attachment, showImage);
  const fileUrl = useFileUrl(showImage ? file : null);
  const name = getAttachmentName(attachment);

  return (
    <chakra.button
      type="button"
      onClick={onSelect}
      width="84px"
      height="110px"
      flexShrink={0}
      borderRadius="5px"
      border="2px solid"
      borderColor={isSelected ? "blue" : "transparent"}
      bg="white"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      title={name}
    >
      {showImage && fileUrl ? (
        <chakra.img
          src={fileUrl}
          alt={name}
          width="100%"
          height="100%"
          objectFit="cover"
        />
      ) : (
        <>
          <Icon as={EmrDocument} h="24px" w="24px" color="gray.650" />
          <Text
            fontSize="0.625rem"
            color="gray.650"
            mt="6px"
            px="4px"
            noOfLines={2}
            wordBreak="break-all"
          >
            {name}
          </Text>
        </>
      )}
    </chakra.button>
  );
}

function AttachmentPreview(props: AttachmentItemProps) {
  const { attachment } = props;
  const { file, contentType, isLoading } = useAttachmentFile(attachment, true);
  const showImage = Boolean(isImage(contentType));
  const imageUrl = useFileUrl(showImage ? file : null);

  if (isLoading) return <Loading />;

  if (!file) {
    return (
      <Center height="100%" color="gray.650">
        {getAttachmentName(attachment)}
      </Center>
    );
  }

  if (showImage) {
    return (
      <Center height="100%" p="10px">
        <chakra.img
          src={imageUrl}
          alt={getAttachmentName(attachment)}
          maxWidth="100%"
          maxHeight="100%"
          objectFit="contain"
        />
      </Center>
    );
  }

  return (
    <PdfViewer key={attachment.key} value={file}>
      <PdfViewerContainer
        height="100%"
        border="none"
        borderRadius="0"
        contentContainerProps={{ height: "100%", minHeight: 0 }}
        activePage={<PdfViewerActivePage fitToContainer />}
        pageController={<PdfViewerPageController />}
      />
    </PdfViewer>
  );
}

function AddendumCard(props: AddendumCardProps) {
  const {
    addendum,
    isDisabled,
    onChangeText,
    onDelete,
    onAddFiles,
    onRemoveAttachment,
  } = props;
  const { createdBy, attachments, isEditable } = addendum;
  const canModify = isEditable && !isDisabled;
  const textareaBorderColor = isEditable ? "blue" : "gray.450";
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  const selectedAttachment =
    attachments.find((attachment) => attachment.key === selectedKey) ??
    attachments[0] ??
    null;

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) return;
    onAddFiles(files);
  };

  return (
    <Card
      padding="16px"
      bg="white"
      boxShadow="none"
      border="none"
      borderRadius="10px"
    >
      <Grid
        gridTemplateColumns={ADDENDUM_GRID_COLUMNS}
        gridColumnGap="18px"
        alignItems="center"
      >
        <Flex alignItems="center">
          <UserAvatar
            mr="11px"
            size="xs"
            w="32px"
            h="32px"
            fontSize=".25rem"
            picture={createdBy.pictureUrl ?? ""}
            name={createdBy.fullName}
          />
          <Box>
            <Text fontWeight="bold" fontSize="1.063rem" lineHeight="1.063rem">
              {createdBy.fullName}
            </Text>
            <Text
              fontSize="0.875rem"
              lineHeight="0.875rem"
              color="gray"
              mt="2px"
            >
              {createdBy.teamType ?? ""}
            </Text>
          </Box>
        </Flex>
        <Box
          justifySelf="center"
          border="1px solid"
          borderColor="blue"
          borderRadius="20px"
          padding="6px 16px"
          fontSize="0.9375rem"
          bg="white"
        >
          {formatAddendumDate(addendum.createdAt)}
        </Box>
        <Flex justifyContent="flex-end">
          {isEditable && (
            <Button
              variant="label"
              color="red"
              onClick={onDelete}
              isDisabled={isDisabled}
              leftIcon={<Icon as={EmrRemove} color="red" />}
            >
              Delete Addendum
            </Button>
          )}
        </Flex>
      </Grid>

      <Box position="relative" mt="12px">
        <Textarea
          value={addendum.text}
          onChange={(event) => onChangeText(event.target.value)}
          isReadOnly={!canModify}
          maxLength={MAX_ADDENDUM_TEXT_LENGTH}
          resize="none"
          minHeight="120px"
          border="1px solid"
          borderColor={textareaBorderColor}
          borderRadius="10px"
          paddingRight="36px"
          _hover={{ borderColor: textareaBorderColor }}
          focusBorderColor={textareaBorderColor}
          _focusVisible={{ borderColor: textareaBorderColor }}
        />
        {canModify && addendum.text.length > 0 && (
          <IconButton
            aria-label="clear addendum text"
            variant="icon"
            size="iconSm"
            position="absolute"
            top="8px"
            right="8px"
            zIndex={1}
            icon={<Icon as={SiClear} width="10px" height="10px" />}
            color="gray.600"
            onClick={() => onChangeText("")}
          />
        )}
      </Box>

      <Grid
        gridTemplateColumns="1fr auto 1fr"
        alignItems="center"
        mt="12px"
        padding="8px 16px"
        bg="gray.50"
        borderRadius="10px"
      >
        <Box />
        <Text fontWeight="600" color="gray.650" fontSize="0.9375rem">
          Attachments
        </Text>
        <HStack justifyContent="flex-end" spacing="1rem">
          {isEditable && (
            <>
              <IconButton
                aria-label="add attachment"
                variant="icon"
                icon={<Icon as={EmrPlus} color="blue" />}
                isDisabled={isDisabled}
                onClick={() => fileInputRef.current?.click()}
              />
              <IconButton
                aria-label="delete attachment"
                variant="icon"
                icon={<Icon as={EmrTrashcan} color="gray.650" />}
                isDisabled={isDisabled || !selectedAttachment}
                onClick={() => {
                  if (selectedAttachment)
                    onRemoveAttachment(selectedAttachment);
                }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT}
                multiple
                style={{ display: "none" }}
                onChange={onFileInputChange}
              />
            </>
          )}
        </HStack>
      </Grid>

      {attachments.length > 0 && selectedAttachment && (
        <Flex mt="12px" height="520px" gap="12px">
          <Flex
            direction="column"
            gap="12px"
            padding="8px"
            bg="gray.200"
            borderRadius="10px"
            overflowY="auto"
            alignItems="center"
          >
            {attachments.map((attachment) => (
              <AttachmentThumbnail
                key={attachment.key}
                attachment={attachment}
                isSelected={attachment.key === selectedAttachment.key}
                onSelect={() => setSelectedKey(attachment.key)}
              />
            ))}
          </Flex>
          <Box
            flex={1}
            minWidth={0}
            bg="gray.200"
            borderRadius="10px"
            border="1px solid"
            borderColor="gray.450"
            overflow="hidden"
          >
            <AttachmentPreview
              key={selectedAttachment.key}
              attachment={selectedAttachment}
            />
          </Box>
        </Flex>
      )}
    </Card>
  );
}

function AddendumsEditor(props: AddendumsEditorProps) {
  const { encounterId, onClose, initialData } = props;
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth0();
  const currentUserId: string =
    typeof user?.[USERID_CLAIMS] === "string" ? user[USERID_CLAIMS] : "";
  const { data: currentUserDetails } = useGetUserDetails(currentUserId, {
    enabled: !!currentUserId,
    currentUser: true,
  });

  const { canAdd, deadline } = initialData;
  const keyCounterRef = React.useRef(0);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const shouldScrollToBottomRef = React.useRef(false);

  const [draft, setDraft] = React.useState<AddendumsDraft>(() => ({
    addendums: initialData.addendums.map((addendum) =>
      toDraftAddendum(addendum, canAdd)
    ),
    removedAddendumIds: [],
  }));
  const [addendumToDelete, setAddendumToDelete] = React.useState<string | null>(
    null
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const discardDisclosure = useDisclosure();

  const { mutateAsync: createAddendum } = useCreateAddendum(encounterId);
  const { mutateAsync: updateAddendum } = useUpdateAddendum();
  const { mutateAsync: deleteAddendum } = useDeleteAddendum();
  const { mutateAsync: uploadAttachment } = useUploadAddendumAttachment();
  const { mutateAsync: deleteAttachment } = useDeleteAddendumAttachment();

  const isDirty =
    draft.removedAddendumIds.length > 0 ||
    draft.addendums.some(isDraftAddendumDirty);
  const hasEmptyText = draft.addendums.some(
    (addendum) =>
      addendum.isEditable &&
      isDraftAddendumDirty(addendum) &&
      addendum.text.trim().length === 0
  );

  React.useEffect(() => {
    if (shouldScrollToBottomRef.current && bodyRef.current) {
      shouldScrollToBottomRef.current = false;
      bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight });
    }
  }, [draft.addendums.length]);

  const nextKey = (prefix: string) => {
    keyCounterRef.current += 1;
    return `${prefix}-${keyCounterRef.current}`;
  };

  const updateAddendumDraft = (
    key: string,
    update: (addendum: DraftAddendum) => DraftAddendum
  ) => {
    setDraft((current) => ({
      ...current,
      addendums: current.addendums.map((addendum) =>
        addendum.key === key ? update(addendum) : addendum
      ),
    }));
  };

  const onAddAddendum = () => {
    const author: AddendumAuthor = {
      id: currentUserId,
      fullName:
        currentUserDetails?.data.fullName ??
        `${user?.given_name ?? ""} ${user?.family_name ?? ""}`.trim(),
      pictureUrl: currentUserDetails?.data.pictureUrl ?? null,
      teamType:
        currentUserDetails?.data.teamTypes
          ?.split(",")
          .map((teamType) => teamType.trim())
          .find((teamType) => teamType.length > 0) ?? null,
    };
    const key = nextKey("new-addendum");
    shouldScrollToBottomRef.current = true;
    setDraft((current) => ({
      ...current,
      addendums: [
        ...current.addendums,
        {
          key,
          id: null,
          text: "",
          savedText: "",
          createdAt: new Date().toISOString(),
          createdBy: author,
          isEditable: true,
          attachments: [],
          removedAttachmentIds: [],
        },
      ],
    }));
  };

  const onConfirmDeleteAddendum = () => {
    if (!addendumToDelete) return;
    setDraft((current) => {
      const target = current.addendums.find(
        (addendum) => addendum.key === addendumToDelete
      );
      return {
        addendums: current.addendums.filter(
          (addendum) => addendum.key !== addendumToDelete
        ),
        removedAddendumIds: target?.id
          ? [...current.removedAddendumIds, target.id]
          : current.removedAddendumIds,
      };
    });
    setAddendumToDelete(null);
  };

  const onAddFiles = (key: string, files: File[]) => {
    const allowedFiles = files.filter(isAllowedAttachment);
    if (allowedFiles.length < files.length) {
      toast({
        description: `Only PDF, PNG, JPG or GIF files up to ${MAX_ATTACHMENT_SIZE_IN_MB} MB can be attached.`,
      });
    }
    if (allowedFiles.length === 0) return;
    const newAttachments: DraftAttachment[] = allowedFiles.map((file) => ({
      kind: "pending",
      key: nextKey("pending-attachment"),
      file,
    }));
    updateAddendumDraft(key, (addendum) => ({
      ...addendum,
      attachments: [...addendum.attachments, ...newAttachments],
    }));
  };

  const onRemoveAttachment = (key: string, attachment: DraftAttachment) => {
    updateAddendumDraft(key, (addendum) => ({
      ...addendum,
      attachments: addendum.attachments.filter(
        (item) => item.key !== attachment.key
      ),
      removedAttachmentIds:
        attachment.kind === "existing"
          ? [...addendum.removedAttachmentIds, attachment.attachment.id]
          : addendum.removedAttachmentIds,
    }));
  };

  const onSave = async () => {
    setIsSaving(true);
    let removedAddendumIds = [...draft.removedAddendumIds];
    const addendums: DraftAddendum[] = draft.addendums.map((addendum) => ({
      ...addendum,
      attachments: [...addendum.attachments],
      removedAttachmentIds: [...addendum.removedAttachmentIds],
    }));

    try {
      for (const addendumId of draft.removedAddendumIds) {
        await deleteAddendum(addendumId);
        removedAddendumIds = removedAddendumIds.filter(
          (id) => id !== addendumId
        );
      }

      for (const addendum of addendums) {
        if (!addendum.isEditable) continue;

        if (addendum.id === null) {
          const { data: created } = await createAddendum({
            text: addendum.text,
          });
          addendum.id = created.id;
          addendum.savedText = addendum.text;
        } else if (addendum.text !== addendum.savedText) {
          await updateAddendum({
            addendumId: addendum.id,
            text: addendum.text,
          });
          addendum.savedText = addendum.text;
        }

        const addendumId = addendum.id;

        for (const attachmentId of [...addendum.removedAttachmentIds]) {
          await deleteAttachment(attachmentId);
          addendum.removedAttachmentIds = addendum.removedAttachmentIds.filter(
            (id) => id !== attachmentId
          );
        }

        for (const attachment of [...addendum.attachments]) {
          if (attachment.kind !== "pending") continue;
          const { data: uploaded } = await uploadAttachment({
            addendumId,
            file: attachment.file,
          });
          addendum.attachments = addendum.attachments.map((item) =>
            item.key === attachment.key
              ? { kind: "existing", key: attachment.key, attachment: uploaded }
              : item
          );
        }
      }

      toast({
        status: "success",
        description: formMessages.updateSuccess("Addendums"),
      });
      onClose();
    } catch (error) {
      setDraft({ addendums, removedAddendumIds });
      toast({ description: extractApiErrorMessage(error) });
    } finally {
      setIsSaving(false);
      queryClient.invalidateQueries(["addendums", { encounterId }]);
    }
  };

  const onCancel = () => {
    if (isDirty) {
      discardDisclosure.onOpen();
      return;
    }
    onClose();
  };

  return (
    <>
      <ModalHeader borderTopRadius="md">
        <Flex justifyContent="space-between" alignItems="center">
          <Button
            variant="label"
            color="blue"
            onClick={onCancel}
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Box fontWeight="600">Addendums</Box>
          <Button
            variant="label"
            color="blue"
            onClick={onSave}
            isLoading={isSaving}
            isDisabled={!isDirty || hasEmptyText || isSaving}
          >
            Save
          </Button>
        </Flex>
      </ModalHeader>
      <ModalBody ref={bodyRef} overflowY="auto" paddingBottom="24px">
        <Flex justifyContent="flex-end" alignItems="center" mb="12px">
          {canAdd ? (
            <Button
              variant="label"
              color="blue"
              onClick={onAddAddendum}
              isDisabled={isSaving}
            >
              <Icon as={SiAddCircle} mr="10px" />
              Add New Addendum
            </Button>
          ) : (
            <Text fontSize="0.875rem" color="gray.650">
              {DEADLINE_NOTE}
              {deadline && ` Deadline: ${formatAddendumDate(deadline)}.`}
            </Text>
          )}
        </Flex>
        <Grid
          gridTemplateColumns={ADDENDUM_GRID_COLUMNS}
          gridColumnGap="18px"
          padding="16px"
          bg="gray.250"
          color="gray.650"
          fontSize="0.9375rem"
          fontWeight="600"
          borderRadius="5px"
          mb="12px"
        >
          <chakra.span textAlign="center">Author</chakra.span>
          <chakra.span textAlign="center">Date / Time</chakra.span>
          <Box />
        </Grid>
        <Flex direction="column" gap="12px">
          {draft.addendums.length === 0 && (
            <Center padding="2rem">
              <Text color="gray.500">No addendums yet.</Text>
            </Center>
          )}
          {draft.addendums.map((addendum) => (
            <AddendumCard
              key={addendum.key}
              addendum={addendum}
              isDisabled={isSaving}
              onChangeText={(text) =>
                updateAddendumDraft(addendum.key, (current) => ({
                  ...current,
                  text,
                }))
              }
              onDelete={() => setAddendumToDelete(addendum.key)}
              onAddFiles={(files) => onAddFiles(addendum.key, files)}
              onRemoveAttachment={(attachment) =>
                onRemoveAttachment(addendum.key, attachment)
              }
            />
          ))}
        </Flex>
      </ModalBody>

      <WarningDialog
        isOpen={!!addendumToDelete}
        title="Warning!"
        mainText="Are you sure you want to Delete this Addendum?"
        cancelLabel="Cancel"
        actionLabel="Delete"
        onCancel={() => setAddendumToDelete(null)}
        onClose={() => setAddendumToDelete(null)}
        onAction={onConfirmDeleteAddendum}
        blockScrollOnMount={false}
      />
      <WarningDialog
        isOpen={discardDisclosure.isOpen}
        title="Warning"
        mainText="You have unsaved changes!"
        cancelLabel="Cancel"
        actionLabel="Discard Changes"
        onCancel={discardDisclosure.onClose}
        onClose={discardDisclosure.onClose}
        onAction={() => {
          discardDisclosure.onClose();
          onClose();
        }}
        blockScrollOnMount={false}
      />
    </>
  );
}

function AddendumsModalContent(props: AddendumsModalContentProps) {
  const { encounterId, onClose } = props;
  const toast = useToast();
  const { data, isFetching, error } = useAddendums(encounterId);
  const [initialData, setInitialData] =
    React.useState<AddendumsResponse | null>(null);

  React.useEffect(() => {
    if (!initialData && data?.data && !isFetching) {
      setInitialData(data.data);
    }
  }, [data, isFetching, initialData]);

  React.useEffect(() => {
    if (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  }, [error, toast]);

  return (
    <ModalContent
      borderRadius="md"
      bgColor="gray.50"
      width="95vw"
      maxWidth="95vw"
      height="92vh"
      marginY="4vh"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {initialData ? (
        <AddendumsEditor
          encounterId={encounterId}
          onClose={onClose}
          initialData={initialData}
        />
      ) : (
        <>
          <ModalHeader borderTopRadius="md">
            <Flex justifyContent="space-between" alignItems="center">
              <Button variant="label" color="blue" onClick={onClose}>
                Cancel
              </Button>
              <Box fontWeight="600">Addendums</Box>
              <Box />
            </Flex>
          </ModalHeader>
          <ModalBody>
            {error ? (
              <Center height="100%">
                <Text color="gray.650">Addendums could not be loaded.</Text>
              </Center>
            ) : (
              <Loading />
            )}
          </ModalBody>
        </>
      )}
    </ModalContent>
  );
}

function AddendumsModal(props: AddendumsModalProps) {
  const { encounterId, isOpen, onClose } = props;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <AddendumsModalContent encounterId={encounterId} onClose={onClose} />
    </Modal>
  );
}

export { AddendumsModal };
